import { safeGet } from "@/modules/utils/safe-get";
import { type TypographyProps } from "@chakra-ui/styled-system";
import { type SelectProps as ChakraSelectProps } from "@chakra-ui/react";
import { injectTypographyVariant } from "@/components/essentials/typography/variant";
import { type SelectProps } from "./Select";
import { type SelectSize, type SelectBreakPointSize, type SelectVariant } from "./type";

interface SelectSizeValue {
  readonly h: SelectProps["h"];
  readonly fontSize: TypographyProps["fontSize"];
  readonly fontWeight: TypographyProps["fontWeight"];
}

const selectSize = {
  base: {
    h: "2rem",
    ...injectTypographyVariant("text4"),
  },
  sm: {
    h: "2.5rem",
    ...injectTypographyVariant("text3"),
  },
  md: {
    h: "2.75rem",
    ...injectTypographyVariant("text2"),
  },
  lg: {
    h: "3rem",
    ...injectTypographyVariant("text1"),
  },
};

const switchSelectSizeVariant = (size: SelectSize): SelectSizeValue => {
  return safeGet(selectSize, size);
};

export const injectSelectSize = (size: SelectSize | SelectBreakPointSize): SelectSizeValue => {
  if (typeof size === "string") {
    const { h, fontSize, fontWeight } = switchSelectSizeVariant(size);
    return {
      h,
      fontSize,
      fontWeight,
    };
  } else {
    const { h: baseH, fontSize: baseFontSize, fontWeight: baseFontWeight } = switchSelectSizeVariant(size.base);
    const { h: smH, fontSize: smFontSize, fontWeight: smFontWeight } = switchSelectSizeVariant(size.sm);
    const { h: mdH, fontSize: mdFontSize, fontWeight: mdFontWeight } = switchSelectSizeVariant(size.md);
    const { h: lgH, fontSize: lgFontSize, fontWeight: lgFontWeight } = switchSelectSizeVariant(size.lg);
    return {
      h: {
        base: baseH as string,
        sm: smH as string,
        md: mdH as string,
        lg: lgH as string,
      },
      fontSize: {
        base: baseFontSize as string,
        sm: smFontSize as string,
        md: mdFontSize as string,
        lg: lgFontSize as string,
      },
      fontWeight: {
        base: baseFontWeight as string,
        sm: smFontWeight as string,
        md: mdFontWeight as string,
        lg: lgFontWeight as string,
      },
    };
  }
};

const baseSelectStyle: ChakraSelectProps = {
  _invalid: {
    boxShadow: "0 0 0 1px var(--chakra-colors-error-main)",
    bg: "error.light",
    _focus: {
      boxShadow: "0 0 0 1px var(--chakra-colors-error-main)",
      borderColor: "error.main",
      bg: "error.light",
    },
  },
  _focus: {
    boxShadow: "0 0 0 1px var(--chakra-colors-secondary-main)",
    borderColor: "secondary.main",
    bg: "light.main",
  },
  errorBorderColor: "error.main",
  focusBorderColor: "secondary.main",
  bg: "background.main",
  borderColor: "background.main",
  borderWidth: "0px",
  _placeholder: { textColor: "gray.main" },
  _hover: {},
};

const monoSelectStyle: ChakraSelectProps = {
  bg: "light.main",
  borderColor: "border.main",
  borderWidth: "1px",
};

export const injectSelectStyle = (variant: SelectVariant): ChakraSelectProps => {
  if (variant === "mono") {
    return { ...baseSelectStyle, ...monoSelectStyle };
  }

  return baseSelectStyle;
};
